<template>
  <div>
    <p class="title is-4">
      {{ exhibitorSuite.name }} <br />
      <span
        class="title is-6"
        v-if="
          exhibitorSuite.company &&
            exhibitorSuite.company.name !== exhibitorSuite.name
        "
      >
        {{ exhibitorSuite.company.name }}
      </span>
    </p>

    <p>
      Below are the appointment times available for {{ exhibitorSuite.name }}.
      Click on <b>Request Appointment</b> to request an appointment for a
      specific date and time. The company contact will receive an e-mail with
      your request.
    </p>

    <section
      class="my-8"
      v-for="meetingDate in sortedActiveScheduleKeys"
      :key="meetingDate"
    >
      <h3 class="subtitle is-5">
        <span class="icon"><i class="far fa-calendar"></i></span>
        <span>{{ formatDate(meetingDate) }}</span>
      </h3>
      <div class="box">
        <table class="table is-hoverable is-fullwidth">
          <thead>
            <tr>
              <th>Time Slot</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(timeslot, timeslotIdx) in activeSchedule[meetingDate]"
              :key="timeslotIdx"
              v-if="timeslot.is_open || hasWriteableSlotAvailable(timeslot)"
            >
              <td width="250">
                {{ `${timeslot.start_time} - ${timeslot.end_time}` }}
              </td>
              <td>
                <span
                  v-for="(userSuite, userSuiteKey) in userWriteableSuites"
                  :key="userSuiteKey"
                >
                  <p
                    v-if="
                      timeslot.pending.findIndex(
                        item =>
                          item.suiteId === userSuiteKey && item.type == 'from'
                      ) < 0 &&
                        isTimeslotOpen(timeslot.id, userSuite.appointment_slots)
                    "
                    class="request-line"
                  >
                    <button
                      type="button"
                      class="button is-small is-primary is-outlined"
                      @click.prevent="
                        confirmRequestAppointment(
                          userSuiteKey,
                          timeslot,
                          meetingDate
                        )
                      "
                    >
                      <span class="icon is-small">
                        <i class="far fa-calendar-plus"></i>
                      </span>
                      <span>Request Appointment</span>
                    </button>
                    <span v-if="Object.keys(userWriteableSuites).length > 1"
                      >for <b>{{ userSuite.name }}</b></span
                    >
                  </p>
                  <p
                    v-else-if="
                      timeslot.pending.findIndex(
                        item =>
                          item.suiteId === userSuiteKey && item.type == 'from'
                      ) >= 0
                    "
                    class="request-line"
                  >
                    <button
                      type="button"
                      class="button is-small is-danger is-outlined"
                      @click.prevent="
                        confirmCancelRequest(
                          userSuiteKey,
                          timeslot,
                          meetingDate
                        )
                      "
                    >
                      <span class="icon is-small">
                        <i class="far fa-calendar-times"></i>
                      </span>
                      <span>Cancel Request</span>
                    </button>
                    <span v-if="Object.keys(userWriteableSuites).length > 1"
                      >for <b>{{ userSuite.name }}</b></span
                    >
                  </p>
                  <div
                    v-if="myOtherPendingRequests(meetingDate, timeslot).length"
                    class="mt-2"
                  >
                    <p><strong>Other requests for this time:</strong></p>
                    <p
                      v-for="item in myOtherPendingRequests(
                        meetingDate,
                        timeslot
                      )"
                      :key="item.suiteId"
                      v-html="item.message"
                    ></p>
                  </div>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <modal
      title="Request Appointment"
      v-if="showConfirmRequestModal"
      :show-close-button="false"
    >
      <table>
        <tbody>
          <tr>
            <td>With:</td>
            <td class="pl-4">
              <strong>{{ exhibitorSuite.name }}</strong>
            </td>
          </tr>
          <tr>
            <td>Date:</td>
            <td class="pl-4">
              <strong>{{
                formatDate(requestAppointmentData.meetingDate)
              }}</strong>
            </td>
          </tr>
          <tr>
            <td>Time:</td>
            <td class="pl-4">
              <strong>{{ requestAppointmentData.timeslot.start_time }}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="field mt-4">
        <div class="control">
          <textarea
            v-model="requestAppointmentData.notes"
            class="textarea"
            placeholder="Include an optional note with the request"
          ></textarea>
        </div>
      </div>

      <div slot="footer">
        <button
          type="button"
          class="button is-primary"
          @click="requestAppointment"
          :class="{ 'is-loading': requestAppointmentData.isLoading }"
          :disabled="requestAppointmentData.isLoading"
        >
          Send Request
        </button>
        <button
          type="button"
          class="button"
          @click="showConfirmRequestModal = false"
          :disabled="requestAppointmentData.isLoading"
        >
          Nevermind
        </button>
      </div>
    </modal>

    <modal
      title="Cancel the Appointment Request"
      v-if="showConfirmCancelModal"
      :show-close-button="false"
    >
      <table>
        <tbody>
          <tr>
            <td>With:</td>
            <td class="pl-4">
              <strong>{{ exhibitorSuite.name }}</strong>
            </td>
          </tr>
          <tr>
            <td>Date:</td>
            <td class="pl-4">
              <strong>{{
                formatDate(cancelAppointmentData.meetingDate)
              }}</strong>
            </td>
          </tr>
          <tr>
            <td>Time:</td>
            <td class="pl-4">
              <strong>{{ cancelAppointmentData.timeslot.start_time }}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="field mt-4">
        <div class="control">
          <textarea
            v-model="cancelAppointmentData.notes"
            class="textarea"
            placeholder="Include an optional note with the cancelation"
          ></textarea>
        </div>
      </div>

      <div slot="footer">
        <button
          type="button"
          class="button is-danger"
          @click="cancelAppointment"
          :class="{ 'is-loading': cancelAppointmentData.isLoading }"
          :disabled="cancelAppointmentData.isLoading"
        >
          Cancel Request
        </button>
        <button
          type="button"
          class="button"
          @click="showConfirmCancelModal = false"
          :disabled="cancelAppointmentData.isLoading"
        >
          Nevermind
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { db, requestAppt, cancelRequest } from '@/services/firebase';
import dbhelpers from '@/services/dbhelpers';
import swal from 'sweetalert';
import {
  formatDate,
  isTimeslotOpen,
  getCompanySuiteName
} from '@/services/utils';
import modal from './Modal';

export default {
  components: {
    modal
  },
  props: {
    suiteId: { type: String },
    id: { type: String }
  },
  data() {
    return {
      userWriteableSuites: {},
      exhibitorSuite: {},
      meeting: {},
      requestAppointmentData: {
        userSuiteKey: '',
        timeslot: {},
        meetingDate: '',
        notes: '',
        isLoading: false
      },
      cancelAppointmentData: {
        userSuiteKey: '',
        timeslot: {},
        meetingDate: '',
        notes: '',
        isLoading: false
      },
      showConfirmCancelModal: false,
      showConfirmRequestModal: false
    };
  },
  computed: {
    ...mapGetters(['meetingSuites']),
    activeSchedule() {
      return Object.keys(this.exhibitorSuite).length &&
        Object.keys(this.exhibitorSuite.appointment_slots).length
        ? this.exhibitorSuite.appointment_slots
        : this.meeting.appointment_slots;
    },
    sortedActiveScheduleKeys() {
      if (!this.activeSchedule) return [];
      const keys = Object.keys(this.activeSchedule) || [];
      return keys.sort();
    }
  },
  methods: {
    confirmRequestAppointment(userSuiteKey, timeslot, meetingDate) {
      this.requestAppointmentData = {
        userSuiteKey,
        timeslot,
        meetingDate,
        notes: '',
        isLoading: false
      };
      this.showConfirmRequestModal = true;
    },
    requestAppointment() {
      this.requestAppointmentData.isLoading = true;
      requestAppt({
        meetingId: this.id,
        appointmentId: this.requestAppointmentData.timeslot.id,
        requestFrom: this.requestAppointmentData.userSuiteKey,
        notes: this.requestAppointmentData.notes,
        requestTo: this.suiteId
      })
        .then(resp => {
          this.showConfirmRequestModal = false;
          swal({
            title: 'Request Sent',
            text: `The appointment request was sent and is pending review by ${this.exhibitorSuite.name}.`,
            icon: 'success',
            buttons: {
              confirm: {
                text: 'OK',
                value: true,
                visible: true,
                className: 'button is-primary is-inverted',
                closeModal: true
              }
            }
          });
          this.requestAppointmentData = {
            userSuiteKey: '',
            timeslot: {},
            meetingDate: '',
            notes: '',
            isLoading: false
          };
        })
        .catch(err => {
          console.error(err);
          this.requestAppointmentData.isLoading = false;
        });
    },
    confirmCancelRequest(userSuiteKey, timeslot, meetingDate) {
      this.cancelAppointmentData = {
        userSuiteKey,
        timeslot,
        meetingDate,
        notes: '',
        isLoading: false
      };
      this.showConfirmCancelModal = true;
    },
    cancelAppointment() {
      this.cancelAppointmentData.isLoading = true;
      cancelRequest({
        meetingId: this.id,
        appointmentId: this.cancelAppointmentData.timeslot.id,
        requestFrom: this.cancelAppointmentData.userSuiteKey,
        notes: this.cancelAppointmentData.notes,
        requestTo: this.suiteId
      })
        .then(resp => {
          this.showConfirmCancelModal = false;
          swal({
            title: 'Request Canceled',
            text: `The appointment request was canceled.`,
            icon: 'success',
            buttons: {
              confirm: {
                text: 'OK',
                value: true,
                visible: true,
                className: 'button is-primary is-inverted',
                closeModal: true
              }
            }
          });
          this.cancelAppointmentData = {
            userSuiteKey: '',
            timeslot: {},
            meetingDate: '',
            notes: '',
            isLoading: false
          };
        })
        .catch(err => {
          console.error(err);
          this.cancelAppointmentData.isLoading = false;
        });
    },
    hasWriteableSlotAvailable(timeslot) {
      return Object.keys(this.userWriteableSuites).some(userSuiteId => {
        return (
          timeslot.pending.findIndex(
            item => item.suiteId === userSuiteId && item.type === 'from'
          ) >= 0
        );
      });
    },
    myOtherPendingRequests(meetingDate, timeslot) {
      const myPending = [];

      Object.keys(this.userWriteableSuites).forEach(userSuiteId => {
        const myTimeslot = this.userWriteableSuites[userSuiteId].appointment_slots[meetingDate].find(slot => slot.id === timeslot.id);

        if (myTimeslot) {
          myTimeslot.pending.forEach(pending => {
            const isMyRequest =
              pending.suiteId === this.suiteId && pending.type === 'to';
            if (!isMyRequest) {
              const newObj = { ...pending };

              const suiteName = this.meetingSuites[pending.suiteId]
                ? getCompanySuiteName(
                    this.meetingSuites[pending.suiteId].name,
                    this.meetingSuites[pending.suiteId].company.name
                  )
                : '';

              if (suiteName) {
                newObj.suiteName = suiteName;
                const subject = pending.type === 'to' ? 'You' : suiteName;
                const obj = pending.type === 'to' ? suiteName : 'you';

                newObj.message = `<strong>${subject}</strong> requested a meeting with <strong>${obj}</strong>`;

                myPending.push(newObj);
              }
            }
          });
        }
      });

      return myPending;
    },
    isTimeslotOpen,
    formatDate
  },
  firestore() {
    return {
      exhibitorSuite: db
        .collection('meetings')
        .doc(this.id)
        .collection('suites')
        .doc(this.suiteId),
      meeting: db.collection('meetings').doc(this.id)
    };
  },
  created() {
    dbhelpers.getUserWriteableSuiteDocs(this.id).forEach(suiteDoc => {
      suiteDoc.onSnapshot(snap => {
        this.$set(this.userWriteableSuites, snap.id, { ...snap.data() });
      });
    });
  }
};
</script>

<style scoped>
p.request-line {
  padding: 0.1em;
}
</style>



// WEBPACK FOOTER //
// src/components/shared/ExhibitorSchedule.vue